<template>
  <div class="position-relative">
    <button
      ref="toggleButton"
      :title="filter.selectedTitle"
      aria-expanded="false"
      aria-haspopup="true"
      class="btn btn-light btn-sm dropdown-toggle text-truncate filter"
      data-bs-toggle="dropdown"
      type="button"
    >
      <span v-html="filter.title"/>: {{ filter.selectedTitle }}
    </button>
    <div
      class="dropdown-menu filter dropdown-menu-autocomplete"
    >
      <div class="px-3 pt-2">
        <div class="mb-3">
          <div class="search-wrap">
            <input
              ref="searchFieldInput"
              v-model="searchPhrase"
              class="form-control form-control-sm"
              type="text"
              @keyup="textSearch"
            >
            <span class="glyphicons search"/>
          </div>
        </div>
        <template v-for="(item, itemIdx) in staticItems">
          <div
            v-if="item.type == 'separator'"
            :key="`autocomplete-separator-${itemIdx}`"
            class="dropdown-divider"
          />
          <a
            v-else
            :key="`autocomplete-item-${itemIdx}`"
            :title="item.title"
            class="dropdown-item text-truncate"
            href="#"
            @click.prevent="selectItem($event, item)"
          >
            <span :class="{ 'glyphicons checkmark': true, invisible: !isSelected(item) }"/>{{ item.title }}
          </a>
        </template>
        <div
          v-if="selectionItems.length > 0"
          class="dropdown-divider"
        />
        <h6
          v-if="selectionItems.length > 0 && ajaxSearch"
          class="dropdown-header"
        >
          Selection
        </h6>
        <a
          v-for="(item, itemIdx) in selectionItems"
          :key="`autocomplete-temporary-item-${itemIdx}`"
          :title="item.title"
          class="dropdown-item text-truncate"
          href="#"
          @click.prevent="selectItem($event, item)"
        >
          <span :class="{ 'glyphicons checkmark': true, invisible: !isSelected(item) }"/>{{ item.title }}
        </a>
        <div
          v-if="hasAutocompleteItems"
          class="dropdown-divider"
        />
        <h6
          v-if="hasAutocompleteItems"
          class="dropdown-header"
        >
          Results
        </h6>
        <a
          v-for="(item, itemIdx) in autocompleteItems"
          :key="`autocomplete-additional-item-${itemIdx}`"
          :title="item.title"
          class="dropdown-item text-truncate"
          href="#"
          @click.prevent="selectItem($event, item)"
        >
          <span :class="{ 'glyphicons checkmark checkmark-light': true, invisible: !isSelected(item) }"/>{{
            item.title
          }}
        </a>
        <div
          v-if="filter.multiple"
          class="apply-wrap"
        >
          <hr>
          <button
            class="btn btn-sm btn-primary"
            @click="applyFilter"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import axios from 'axios'
import Rails from '@rails/ujs'
import {Dropdown} from 'bootstrap'

import type {GridFilter, GridFilterListItem} from './types'

import {isNonEmptyString} from '../js-utils'
import {computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch} from "vue";

const model = defineModel<string[]>({required: true})

const props = defineProps({
  filter: {type: Object as () => GridFilter, required: true}
})

const ajaxSearch = computed((): boolean => {
  return isNonEmptyString(props.filter.autocompleteUrl)
})

const setItems = () => {
  const listItems = props.filter.listItems || []
  staticItems.value = listItems.filter((item) => {
    return item.type != 'separator' && !isNonEmptyString(item.value)
  })

  if (ajaxSearch.value) {
    autocompleteItems.value = []
    return
  }
}

const toggleButtonRef = useTemplateRef<HTMLButtonElement>("toggleButton")
const searchFieldInputRef = useTemplateRef<HTMLInputElement>("searchFieldInput")

const isLoading = ref(false)
const searchPhrase = ref("")
const searchTimeoutID = ref<number | undefined>(undefined)
const searchText = ref<string | null>(null)

const staticItems = ref<GridFilterListItem[]>([])
const autocompleteItems = ref<GridFilterListItem[]>([])


const getSelectionItems = (clearUnselected: boolean): GridFilterListItem[] => {
  if (ajaxSearch.value) {
    return props.filter.selectedItems.filter((item) => {
      if (!isNonEmptyString(item.value)) {
        return false
      }

      return clearUnselected ? isSelected(item) : true
    })
  }

  const aSearchPhrase = searchPhrase.value.toLowerCase().trim()

  return props.filter.listItems.filter((item) => {
    if (!isNonEmptyString(item.value)) {
      return false // return items except "Any" item
    }

    if (aSearchPhrase == "") {
      return true // return all items
    }

    // selected or matching search criteria
    return isSelected(item) || item.title.toLowerCase().includes(aSearchPhrase)
  })
}

const selectionItems = ref<GridFilterListItem[]>(getSelectionItems(false))

setItems()

const getSelectedValues = (newValue: string[]): string[] => {
  let result: string[] = newValue.slice()
  if (result.length == 0) {
    result = ['']
  }

  return result
}

const selectedValues = ref<string[]>(getSelectedValues(model.value))

const hasAutocompleteItems = computed(() => {
  return autocompleteItems.value.length > 0
})

watch(
  () => model.value,
  (newValue) => {
    selectedValues.value = getSelectedValues(newValue)
    setItems()
  },
  {
    deep: true
  }
)

onMounted(() => {
  const toggleEl = toggleButtonRef.value
  if (!toggleEl) {
    return
  }

  // keep the menu below the button, don't flip it up
  Dropdown.getOrCreateInstance(toggleEl, {
    popperConfig: (defaultConfig) => ({
      ...defaultConfig,
      modifiers: [...defaultConfig.modifiers, {name: 'flip', enabled: false}],
    }),
  })

  toggleEl.addEventListener('shown.bs.dropdown', () => {
    searchPhrase.value = ""
    searchFieldInputRef.value?.focus()
    selectedValues.value = getSelectedValues(model.value)
    selectionItems.value = getSelectionItems(true)
  })
})

onBeforeUnmount(() => {
  if (toggleButtonRef.value) {
    Dropdown.getInstance(toggleButtonRef.value)?.dispose()
  }
})

const isSelected = (item: GridFilterListItem): boolean => {
  return selectedValues.value.indexOf(item.value) != -1
}

const selectItem = (e: Event, item: GridFilterListItem): void => {
  if (props.filter.multiple) {
    if (isNonEmptyString(item.value)) {
      e.stopPropagation()
      const elIdx = selectedValues.value.indexOf(item.value)
      if (elIdx == -1) {
        if (isNonEmptyString(item.value)) {
          // remove empty value, if it's in the list of selected
          selectedValues.value = selectedValues.value.filter((value) => isNonEmptyString(value))
        }

        selectedValues.value.push(item.value)
        // check if item should be added to temporary items
        const tempIdx = selectionItems.value.findIndex((tmpItem) => tmpItem.value == item.value)
        if (tempIdx == -1) {
          // need to add item to temporary items
          selectionItems.value.push(JSON.parse(JSON.stringify(item)))
          // sort temporaryItems
          selectionItems.value.sort((a, b) => {
            return a.value.localeCompare(b.value)
          })
        }
      } else {
        selectedValues.value.splice(elIdx, 1)
        if (selectedValues.value.length == 0) {
          selectedValues.value = ['']
        }
      }
    } else {
      // 'Any' item selected, clear all values
      selectionItems.value = []
      selectedValues.value = ['']
      applyFilter()
    }
  } else {
    selectionItems.value = []
    if (isNonEmptyString(item.value)) {
      selectedValues.value = [item.value]
      selectionItems.value.push(JSON.parse(JSON.stringify(item)))
    } else {
      selectedValues.value = ['']
    }
    applyFilter()
  }
}

const applyFilter = (): void => {
  if (ajaxSearch.value) {
    // This is a hack by editing prop value directly
    // Filters should be reloaded on grid reload with appropriate selected values
    for (const item of selectionItems.value) {
      if (props.filter.listItems.findIndex((selectedItem) => selectedItem.value == item.value) == -1) {
        props.filter.listItems.push(item)
      }
    }
  }
  model.value = selectedValues.value
}

const textSearch = (): void => {
  if (!ajaxSearch.value) {
    selectionItems.value = getSelectionItems(false)
    return
  }

  if (searchTimeoutID.value) {
    clearTimeout(searchTimeoutID.value)
  }

  const aSearchPhrase = searchPhrase.value
  searchTimeoutID.value = window.setTimeout(() => performAjaxTextSearch(aSearchPhrase), 400)
}

const performAjaxTextSearch = (searchPhrase: string): void => {
  if (searchPhrase === searchText.value) {
    return
  }

  if (!isLoading.value) {
    searchText.value = searchPhrase
    loadItems()
  }
}

const parseData = (items: any): void => {
  const newItems: GridFilterListItem[] = []
  for (const item of items.results) {
    newItems.push({type: 'item', value: item.id.toString(), title: item.text})
  }
  autocompleteItems.value = newItems
}

const loadItems = (): void => {
  const autocompleteUrl = props.filter.autocompleteUrl
  if (isNonEmptyString(autocompleteUrl)) {

    isLoading.value = true
    axios.post(autocompleteUrl, {
      term: searchText.value
    }, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': Rails.csrfToken()
      }
    })
      .then((response) => {
        parseData(response.data)
      })
      .catch(() => {
        // pass
      })
      .then(() => {
        isLoading.value = false
        if (searchText.value !== searchPhrase.value) {
          performAjaxTextSearch(searchPhrase.value)
        }
      })
  }
}
</script>
